import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import { readRoomSale, readRoomSales } from '@/api/core/room-sales';
import type { PropertyRuntime } from './property-queries';

export type RoomSalesRuntime = PropertyRuntime & { salesReadEnabled?: boolean };

export function hasRoomSalesReadAccess(runtime: RoomSalesRuntime, generation: number): boolean {
  const state = runtime.session.getSnapshot();
  return (
    runtime.salesReadEnabled === true &&
    state.generation === generation &&
    state.status === 'authorized' &&
    state.authorization !== null &&
    !['property', 'inventory', 'booking'].some((domain) => state.deniedDomains.includes(domain)) &&
    canReadDomain(state.authorization, 'property', 1) &&
    canReadDomain(state.authorization, 'inventory', 16) &&
    canReadDomain(state.authorization, 'booking', 2)
  );
}

const permissionIdentity = (runtime: RoomSalesRuntime) => {
  const state = runtime.session.getSnapshot();
  return JSON.stringify({ authorization: state.authorization, deniedDomains: state.deniedDomains });
};

export const roomSalesLocalStateKey = (
  runtime: RoomSalesRuntime,
  generation: number,
  propertyId: string,
) =>
  JSON.stringify({
    namespace: runtime.apiNamespace,
    generation,
    propertyId,
    permission: permissionIdentity(runtime),
    enabled: runtime.salesReadEnabled === true,
  });

export const roomSalesQueryKey = (
  runtime: RoomSalesRuntime,
  generation: number,
  propertyId: string,
  cursor: string | null,
) =>
  [
    ...coreQueryKey(runtime.apiNamespace, generation, 'room-sales-read', propertyId),
    permissionIdentity(runtime),
    runtime.salesReadEnabled === true,
    cursor,
  ] as const;

export const roomSaleQueryKey = (
  runtime: RoomSalesRuntime,
  generation: number,
  propertyId: string,
  roomId: string,
) =>
  [
    ...coreQueryKey(runtime.apiNamespace, generation, 'room-sales-read', propertyId),
    permissionIdentity(runtime),
    runtime.salesReadEnabled === true,
    'room',
    roomId,
  ] as const;

function scopeCurrent(runtime: RoomSalesRuntime, generation: number, isCurrent: () => boolean) {
  return (
    !runtime.session
      .getSnapshot()
      .deniedDomains.some((domain) => ['property', 'inventory', 'booking'].includes(domain)) &&
    hasRoomSalesReadAccess(runtime, generation) &&
    isCurrent()
  );
}

async function readCurrent<T>(
  runtime: RoomSalesRuntime,
  generation: number,
  signal: AbortSignal,
  isCurrent: () => boolean,
  read: () => Promise<T>,
): Promise<T> {
  if (!scopeCurrent(runtime, generation, isCurrent))
    throw new Error('현재 객실 판매 현황 조회 범위를 확인할 수 없습니다.');
  const result = await read();
  if (signal.aborted || !scopeCurrent(runtime, generation, isCurrent))
    throw new Error('현재 객실 판매 현황 조회 범위를 확인할 수 없습니다.');
  return result;
}

export function roomSalesPageQuery(
  runtime: RoomSalesRuntime,
  generation: number,
  propertyId: string,
  cursor: string | null,
  enabled: boolean,
  isCurrent: () => boolean,
) {
  return {
    queryKey: roomSalesQueryKey(runtime, generation, propertyId, cursor),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      readCurrent(runtime, generation, signal, isCurrent, () =>
        readRoomSales(runtime.client, propertyId, { limit: 20, cursor }, signal),
      ),
    enabled: enabled && hasRoomSalesReadAccess(runtime, generation),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  };
}

export function roomSaleDetailQuery(
  runtime: RoomSalesRuntime,
  generation: number,
  propertyId: string,
  roomId: string | null,
  enabled: boolean,
  isCurrent: () => boolean,
) {
  return {
    queryKey: roomId
      ? roomSaleQueryKey(runtime, generation, propertyId, roomId)
      : roomSalesQueryKey(runtime, generation, propertyId, null),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      roomId
        ? readCurrent(runtime, generation, signal, isCurrent, () =>
            readRoomSale(runtime.client, roomId, signal),
          )
        : Promise.reject(new Error('선택한 객실이 없습니다.')),
    enabled: enabled && roomId !== null && hasRoomSalesReadAccess(runtime, generation),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  };
}
